import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import {
  ApiError,
  BulkFlagSet,
  bulkFlagItems,
} from '../../api';
import { useAuth } from '../../auth/AuthContext';

// Tri-state per flag: "on" / "off" / "leave alone". The radio group's
// `null` value maps to "no write" — the backend reads `null` as "skip
// this flag" so we never accidentally clobber a column the operator
// didn't intend to touch.
type TriState = null | true | false;

type Props = {
  open: boolean;
  tenant: string;
  categoryId: string;
  categoryName: string;
  /** Default for the "Include subcategories" checkbox on first open.
   *  Caller passes `true` for branch categories (where the items live
   *  in descendants), `false` for leaf categories (where direct items
   *  are the natural target). */
  defaultRecursive: boolean;
  onClose: () => void;
  /** Called after a successful Apply so the parent can refresh its
   *  item grid — applied flag changes may have flipped visible state
   *  (e.g. hidden = true → items dim). */
  onApplied: () => void;
};

const FLAG_LABELS: Record<keyof BulkFlagSet, string> = {
  special: 'Special',
  hidden: 'Hidden',
  discontinued: 'Discontinued',
  taxExempt: 'Zero Rated',
};

const FLAGS: (keyof BulkFlagSet)[] = [
  'special',
  'hidden',
  'discontinued',
  'taxExempt',
];

const BulkFlagDialog: React.FC<Props> = ({
  open,
  tenant,
  categoryId,
  categoryName,
  defaultRecursive,
  onClose,
  onApplied,
}) => {
  const { idToken } = useAuth();
  const [recursive, setRecursive] = useState(defaultRecursive);
  const [special, setSpecial] = useState<TriState>(null);
  const [hidden, setHidden] = useState<TriState>(null);
  const [discontinued, setDiscontinued] = useState<TriState>(null);
  // Whole categories tend to be exempt or not as a group (gift cards,
  // say), which is exactly what this dialog is for.
  const [taxExempt, setTaxExempt] = useState<TriState>(null);

  const [count, setCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  const [error, setError] = useState<string>('');
  // Whether the Apply request is in flight. Distinguished from the
  // count fetch so we can disable the Apply button without flicker
  // every time the operator clicks a tri-state radio.
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState<{
    matched: number;
    modified: number;
  } | null>(null);

  // Reset transient state every time the dialog opens. Without this,
  // a previous run's "applied" banner would persist into the next
  // session.
  useEffect(() => {
    if (open) {
      setRecursive(defaultRecursive);
      setSpecial(null);
      setHidden(null);
      setDiscontinued(null);
      setTaxExempt(null);
      setCount(null);
      setError('');
      setApplying(false);
      setResult(null);
    }
  }, [open, defaultRecursive]);

  // Fetch the count whenever `recursive` flips. The count doesn't
  // depend on the flag selections (the filter is the same regardless),
  // so this only refires on toggle — not on every flag click.
  const lastCountRef = useRef<string>('');
  useEffect(() => {
    if (!open || !idToken) return;
    const key = `${categoryId}|${recursive}`;
    if (lastCountRef.current === key) return;
    lastCountRef.current = key;
    let cancelled = false;
    setCountLoading(true);
    bulkFlagItems(idToken, tenant, {
      categoryId,
      recursive,
      set: {},
      apply: false,
    })
      .then((r) => {
        if (!cancelled) setCount(r.matched);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          e instanceof ApiError
            ? `API error ${e.status}`
            : (e as Error)?.message || 'count failed',
        );
        setCount(null);
      })
      .finally(() => {
        if (!cancelled) setCountLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, idToken, tenant, categoryId, recursive]);

  // Reset the "last seen" key when the dialog closes so the next open
  // re-fetches even if recursive happens to be the same value.
  useEffect(() => {
    if (!open) lastCountRef.current = '';
  }, [open]);

  const hasFlag =
    special !== null ||
    hidden !== null ||
    discontinued !== null ||
    taxExempt !== null;

  const set: BulkFlagSet = useMemo(
    () => ({
      ...(special !== null && { special }),
      ...(hidden !== null && { hidden }),
      ...(discontinued !== null && { discontinued }),
      ...(taxExempt !== null && { taxExempt }),
    }),
    [special, hidden, discontinued, taxExempt],
  );

  const triValueFor = (flag: keyof BulkFlagSet): TriState => {
    if (flag === 'special') return special;
    if (flag === 'hidden') return hidden;
    if (flag === 'taxExempt') return taxExempt;
    return discontinued;
  };
  const setTriValueFor = (flag: keyof BulkFlagSet, v: TriState) => {
    if (flag === 'special') setSpecial(v);
    else if (flag === 'hidden') setHidden(v);
    else if (flag === 'taxExempt') setTaxExempt(v);
    else setDiscontinued(v);
  };

  const handleApply = async () => {
    if (!idToken || !hasFlag || count === null) return;
    setApplying(true);
    setError('');
    try {
      const r = await bulkFlagItems(idToken, tenant, {
        categoryId,
        recursive,
        set,
        apply: true,
      });
      setResult({ matched: r.matched, modified: r.modified });
      onApplied();
    } catch (e) {
      setError(
        e instanceof ApiError
          ? `API error ${e.status}`
          : (e as Error)?.message || 'apply failed',
      );
    } finally {
      setApplying(false);
    }
  };

  const handleClose = () => {
    if (applying) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Bulk edit — {categoryName || 'category'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={recursive}
                  onChange={(e) => setRecursive(e.target.checked)}
                  size="small"
                  disabled={applying || result !== null}
                />
              }
              label="Include subcategories"
            />
            <Typography
              variant="body2"
              sx={{ color: '#475569', fontStyle: countLoading ? 'italic' : 'normal' }}
            >
              {countLoading
                ? 'Counting…'
                : count !== null
                  ? `${count} item${count === 1 ? '' : 's'}`
                  : '—'}
            </Typography>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          {result !== null && (
            <Alert severity="success">
              Applied. {result.modified} item{result.modified === 1 ? '' : 's'}{' '}
              changed
              {result.matched !== result.modified
                ? `; ${result.matched - result.modified} already in target state`
                : ''}
              .
            </Alert>
          )}

          {result === null && (
            <Stack spacing={1.5}>
              {FLAGS.map((flag) => (
                <Box key={flag}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: '#1f2937', mb: 0.5 }}
                  >
                    {FLAG_LABELS[flag]}
                  </Typography>
                  <RadioGroup
                    row
                    value={
                      triValueFor(flag) === null
                        ? 'leave'
                        : triValueFor(flag) === true
                          ? 'on'
                          : 'off'
                    }
                    onChange={(e) => {
                      const v = e.target.value;
                      setTriValueFor(
                        flag,
                        v === 'on' ? true : v === 'off' ? false : null,
                      );
                    }}
                  >
                    <FormControlLabel
                      value="on"
                      control={<Radio size="small" disabled={applying} />}
                      label="Set on"
                    />
                    <FormControlLabel
                      value="off"
                      control={<Radio size="small" disabled={applying} />}
                      label="Set off"
                    />
                    <FormControlLabel
                      value="leave"
                      control={<Radio size="small" disabled={applying} />}
                      label="Leave alone"
                    />
                  </RadioGroup>
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={applying}
          sx={{ textTransform: 'none' }}
        >
          {result !== null ? 'Close' : 'Cancel'}
        </Button>
        {result === null && (
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={!hasFlag || count === null || applying || countLoading}
            sx={{ textTransform: 'none' }}
            startIcon={applying ? <CircularProgress size={14} /> : undefined}
          >
            {applying
              ? 'Applying…'
              : count !== null
                ? `Apply to ${count} item${count === 1 ? '' : 's'}`
                : 'Apply'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkFlagDialog;
